import {
	Box,
	Collapse,
	MenuItem,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ColorScheme } from "../../../Themes/ColorScheme";
import { ApiContext } from "../../ApiContext";
import { AlertBar } from "../../Common/AlertBar";
import { UserContext } from "../../UserContext";
import BillingInformationDialog from "./BillingInformationDialog";
import { CustomizeButton } from "./Buttons/CustomizeButton";
import { FormCancel } from "./Buttons/FormCancel";
import { FormEdit } from "./Buttons/FormEdit";
import { FormSave } from "./Buttons/FormSave";

import OPERATORS from "../../../Helpers/OperatorList.json";

export const BillingDetail = ({ data }) => {
	const debug = false;

	const [editMode, setEditMode] = useState(false);
	const [showError, setShowError] = useState(false);

	const [billingData, setBillingData] = useState({
	});
	const [saving, setSaving] = useState(false);

	const [showBillingDialog, setShowBillingDialog] = useState(false);
	const [hover, setHover] = useState(false);

	const { t } = useTranslation();
	const { ApiCall } = useContext(ApiContext);

	const { selectedLocation } = useContext(UserContext);

	const cancelAction = () => {
		fetchBillingData();
		setEditMode(!editMode);
	};

	const handleSave = () => {
		setSaving(true);
		let newBillingData = {
			business_id: billingData.business_id,
			customer_name: billingData.customer_name,
			invoice_operator: billingData.invoice_operator,
			invoice_address: billingData.invoice_address,
			invoice_reference: billingData.invoice_reference,
			contact_name: billingData.contact_name,
			contact_email: billingData.contact_email
		};
		ApiCall("PUT", `client/location/billing/${data.id}`, newBillingData)
			.then(() => {
				setEditMode(!editMode);
				fetchBillingData();
				setSaving(false);
			})
			.catch((error) => {
				if (debug) console.log(error);
				setSaving(false);
			});
	};

	const findBrokerData = (props) => {
		// backup method to find broker data manually if automatic fetch fails for some reason

		const check = OPERATORS.find((entry) => entry.broker_id === props);

		if (check) setBillingDataValue("invoice_operator", check.broker_id);
	};

	function handleAutomaticBillingData(_data) {

		let newBillingData = {
			business_id: _data.business_id,
			customer_name: _data.customer_name,
			invoice_operator: _data.invoice_operator,
			invoice_address: _data.invoice_address,
			invoice_reference: billingData.invoice_reference || "",
			contact_name: billingData.contact_name || "",
   			contact_email: billingData.contact_email || ""
		};

		setBillingData(newBillingData);

		setShowBillingDialog(false);
	}

	function setBillingDataValue(key, value) {
		setBillingData({ ...billingData, [key]: value });
	}

	function fetchBillingData() {

		ApiCall("GET", `client/location/billing/${data.id}`)
			.then((response) => {
				setBillingData(response);
			})
			.catch((error) => {
				if (debug) console.log(error);
			});
	}

	const canNotSave = () => {
		return !(
			billingData.business_id !== "" &&
			billingData.customer_name !== "" &&
			billingData.invoice_operator !== "" &&
			billingData.invoice_address !== "" &&
			billingData.contact_name !== "" &&
			billingData.contact_email !== ""
		);
 	}

	useEffect(() => {

		setShowError(false);
		setEditMode(false);

		fetchBillingData();
	
	}, [selectedLocation]);

	return (
		<Stack
			direction="row"
			justifyContent="space-between"
			sx={{
				padding: "15px",
				border: editMode ? "1px dashed " + ColorScheme.secondary : "0px",
				borderRadius: "4px"
			}}
		>
			<div
				style={{
					width: "100%",
					flexDirection: "row",
					display: "flex",
					flex: 1,
					justifyContent: "flex-start"
				}}
			>
				<div style={{ minWidth: isMobile ? "100px" : "150px" }}>
					<Typography variant="form_text_label">
						{t("billing.form_billing_details_label")}:
					</Typography>
				</div>

				<Stack
					spacing={1}
					sx={{
						width: "100%",
					}}
				>
					<Collapse
						in={
							editMode &&
							(billingData.business_id !== "" || billingData.customer_name !== "")
						}
					>
						<AlertBar
							type="info"
							textContent={t("billing.billing_data_fetch_guide")}
							showAlert={true}
							whiteSpacePreLine={true}
						/>
						<div
							onClick={() =>
								setShowBillingDialog(!showBillingDialog)
							}
							onMouseEnter={() => {
								setHover(true);
							}}
							onMouseLeave={() => {
								setHover(false);
							}}
							style={{
								height: "50px",
								width: "100%",
								backgroundColor: hover
										? ColorScheme.buttonPrimaryHover
										: ColorScheme.buttonPrimary,

								borderRadius: "4px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								cursor: "pointer",
								transition: "background-color 0.2s ease"
							}}
						>
							<Typography
								variant="form_text"
							>
								{t("billing.get_billing_data_automatically")}
							</Typography>
						</div>
						<BillingInformationDialog
							businessId={billingData.business_id}
							customerName={billingData.customer_name}
							passBillingData={handleAutomaticBillingData}
							open={showBillingDialog}
							setOpen={setShowBillingDialog}
						></BillingInformationDialog>
					</Collapse>

					{editMode ? (
						<FormEdit
							title={t("billing.form_client_name_label")}
							value={billingData.customer_name}
							changeData={(value) => setBillingDataValue("customer_name", value)}
							isRequired={true}
						/>
					) : (
						<Typography variant="form_text">
							{billingData.customer_name}
						</Typography>
					)}

					{editMode ? (
						<FormEdit
							title={t("billing.form_business_code_label")}
							value={billingData.business_id}
							changeData={(value) => setBillingDataValue("business_id", value)}
							onlyNumeric={true}
							type={"tel"}
							isRequired={true}
						/>
					) : (
						<Typography variant="form_text">
							{billingData.business_id}
						</Typography>
					)}

					{editMode ? (
						<>
							<Typography>
								{t("location.form_select_broker")}
							</Typography>
							<TextField
								sx={{
									color: ColorScheme.secondary,
									backgroundColor: ColorScheme.white,
									borderRadius: "4px",
									border: "0px"
								}}
								select
								onChange={(selected) => {
									findBrokerData(selected.target.value);
								}}
							>
								{OPERATORS.sort((a, b) =>
									a.name.localeCompare(b.name)
								).map((entry, index) => (
									<MenuItem
										key={index}
										value={entry.broker_id}
									>
										{entry.broker_id} - {entry.name}
									</MenuItem>
								))}
							</TextField>
							<FormEdit
								title={t(
									"billing.form_einvoicing_operator_label"
								)}
								value={billingData.invoice_operator}
								changeData={(value) =>
									setBillingDataValue("invoice_operator", value)
								}
								isRequired={true}
							/>
						</>
					) : (
						<Typography variant="form_text">
							{billingData.invoice_operator}
						</Typography>
					)}
					{editMode ? (
						<FormEdit
							title={t("billing.form_einvoicing_address_label")}
							value={billingData.invoice_address}
							changeData={(value) => setBillingDataValue("invoice_address", value)}
							isRequired={true}
						/>
					) : (
						<Typography variant="form_text">
							{billingData.invoice_address}
						</Typography>
					)}
					{editMode ? (
						<FormEdit
							isRequired={false}
							title={t("billing.form_einvoicing_reference_label")}
							value={billingData.invoice_reference}
							changeData={(value) =>
								setBillingDataValue("invoice_reference", value)
							}
						/>
					) : (
						<Typography variant="form_text">
							{billingData.invoice_reference}
						</Typography>
					)}
					{editMode ? (
						<FormEdit
							isRequired
							title={t("billing.form_business_contact_person")}
							value={billingData.contact_name}
							changeData={(value) => setBillingDataValue("contact_name", value)}
						/>
					) : (
						<Typography variant="form_text">
							{billingData.contact_name}
						</Typography>
					)}
					{editMode ? (
						<FormEdit
							isRequired
							title={t("billing.form_business_contact_email")}
							value={billingData.contact_email}
							changeData={(value) => setBillingDataValue("contact_email", value)}
						/>
					) : (
						<Typography variant="form_text">
							{billingData.contact_email}
						</Typography>
					)}
					{editMode && (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								justifyContent: "flex-end"
							}}
						>
							<FormCancel disabled={saving} cancelAction={cancelAction} />
							<FormSave check={canNotSave()} handleSave={handleSave} activity={saving} />
						</Box>
					)}

					<AlertBar
						showAlert={showError}
						type="warning"
						textContent={t("billing.id_not_found")}
					/>
				</Stack>
			</div>
			{!editMode && (
				<CustomizeButton
					changeEditMode={() => setEditMode(!editMode)}
				/>
			)}
		</Stack>
	);
};
